import React, { useState } from 'react'
import { AlertTriangle, AlertCircle, Info, X } from 'lucide-react'
import type { AlertSeverity, EmployeeVacationAlert } from '@/types/vacation'

interface VacationAlertBannerProps {
  severity: AlertSeverity
  message: string
  dismissible?: boolean
  onDismiss?: () => void
  className?: string
}

export const VacationAlertBanner: React.FC<VacationAlertBannerProps> = ({
  severity,
  message,
  dismissible = true,
  onDismiss,
  className = ''
}) => {
  const [isDismissed, setIsDismissed] = useState(false)

  const handleDismiss = () => {
    setIsDismissed(true)
    if (onDismiss) {
      onDismiss()
    }
  }

  if (isDismissed) {
    return null
  }

  const severityConfig = {
    critical: {
      bgColor: 'bg-error/10',
      borderColor: 'border-error/30',
      textColor: 'text-error',
      icon: AlertTriangle,
      iconColor: 'text-error'
    },
    warning: {
      bgColor: 'bg-warning/10',
      borderColor: 'border-warning/30',
      textColor: 'text-warning',
      icon: AlertCircle,
      iconColor: 'text-warning'
    },
    info: {
      bgColor: 'bg-info/10',
      borderColor: 'border-info/30',
      textColor: 'text-info',
      icon: Info,
      iconColor: 'text-info'
    }
  }

  const config = severityConfig[severity]
  const Icon = config.icon

  return (
    <div
      className={`${config.bgColor} border ${config.borderColor} rounded-monumenta p-4 ${className}`}
      role="alert"
      aria-live={severity === 'critical' ? 'assertive' : 'polite'}
    >
      <div className="flex items-start justify-between">
        <div className="flex items-center space-x-3">
          <Icon
            className={`${config.iconColor} flex-shrink-0`}
            size={20}
            aria-hidden="true"
          />
          <p className={`font-display ${config.textColor}`}>
            {message}
          </p>
        </div>
        {dismissible && (
          <button
            onClick={handleDismiss}
            className="btn-icon btn-ghost text-brand-gray hover:text-white"
            aria-label="Fechar alerta"
          >
            <X size={16} />
          </button>
        )}
      </div>
    </div>
  )
}

interface VacationAlertsContainerProps {
  alerts: EmployeeVacationAlert[]
  onDismiss?: (alertId: string) => void
  className?: string
}

/**
 * Container for multiple alert banners
 * Stacks alerts vertically
 */
export const VacationAlertsContainer: React.FC<VacationAlertsContainerProps> = ({
  alerts,
  onDismiss,
  className = ''
}) => {
  if (alerts.length === 0) {
    return null
  }

  return (
    <div className={`space-y-4 ${className}`}>
      {alerts.map((alert) => (
        <VacationAlertBanner
          key={alert.id}
          severity={alert.severity}
          message={alert.message}
          dismissible={alert.dismissible}
          onDismiss={() => onDismiss?.(alert.id)}
        />
      ))}
    </div>
  )
}
